import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHasMounted } from 'components/_shared/widgets/render-control';
import { useOnScreen } from 'hooks/display';
import {
  CASCADE_EASING,
  CASCADE_TICK_MS,
  DIGITS_PER_REVOLUTION,
  INCREMENT_JITTER_FACTOR,
  INCREMENT_REROLL_MS,
  INCREMENT_SMOOTH_TAU_MS,
  MAX_FRAME_GAP_MS,
  MAX_REEL_REVS_PER_S,
  MS_PER_SECOND,
} from './constants';
import DigitReel from './digit-reel';
import { CounterHeading, Separator } from './styles';
import { FullCounterConfigs } from './types';
import {
  buildTemplate,
  cascadeStepMs,
  computeInitialValue,
  digitAtPlace,
  digitCountOf,
  phaseOf,
  reelDigitsPerSecond,
  reelTransform,
  tokenizeTemplate,
} from './utils';

type ReelRuntime = { displayed: number; animating: boolean };

const BhagCounter = ({
  value,
  incrementValue,
  lastUpdatedAt,
}: FullCounterConfigs) => {
  const hasMounted = useHasMounted();

  /**
   * Only calculate the time elapsed once the component has mounted.
   * This is required to prevent a hydration mismatch.
   */
  const initialValue = useMemo(
    () =>
      hasMounted
        ? computeInitialValue({ value, incrementValue, lastUpdatedAt })
        : Math.round(value),
    [hasMounted, value, incrementValue, lastUpdatedAt]
  );

  /**
   * The reel count grows with the value: the live total can roll past a
   * power-of-ten boundary (e.g. 9,999 -> 10,000) mid-animation, so we track the
   * digit count in state and let the loop bump it as each boundary is crossed.
   * It only ever grows, so the leading reel always holds a real digit (never a
   * padding zero).
   */
  const [digitCount, setDigitCount] = useState(() =>
    digitCountOf(initialValue)
  );

  const template = useMemo(() => buildTemplate(digitCount), [digitCount]);
  const tokens = useMemo(() => tokenizeTemplate(template), [template]);

  const cascadeRefs = useRef<Map<number, HTMLSpanElement>>(new Map());
  const continuousRefs = useRef<Map<number, HTMLSpanElement>>(new Map());

  // reels register their element here so the animation loop can drive them
  const registerReel = useCallback(
    (place: number, continuous: boolean, el: HTMLSpanElement | null) => {
      const reels = continuous ? continuousRefs.current : cascadeRefs.current;
      if (el) {
        reels.set(place, el);
      } else {
        reels.delete(place);
      }
    },
    []
  );

  // used to pause the counter when it is not on screen
  const rootRef = useRef<HTMLHeadingElement>(null);
  const onScreen = useOnScreen({
    ref: rootRef,
    rootMargin: '0px',
    showByDefault: true,
  });
  const loopControlRef = useRef<{ start: () => void; stop: () => void }>();

  /**
   * The single animation loop. Each frame it eases the pace toward a periodically
   * re-rolled target, advances the one true value, then positions every reel from
   * it: fast reels lock to it via transform, slow reels cascade one digit at a
   * time. Reading the same value keeps every reel's carries aligned as the pace
   * drifts.
   */
  useEffect(() => {
    if (incrementValue <= 0) return;

    let liveValue = initialValue;
    // liveValue needs an extra reel once it reaches this power of ten
    let nextDigitThreshold = 10 ** digitCountOf(Math.floor(initialValue));
    // liveIncrement drifts toward a re-rolled target for the randomisation effect
    let liveIncrement = incrementValue;
    let targetIncrement = incrementValue;
    let nextRerollAt = 0;
    const rerollTarget = () => {
      const jitter = incrementValue * INCREMENT_JITTER_FACTOR;
      // Math.random() is [0, 1); (× 2 − 1) maps it to [−1, 1) for a ± swing.
      targetIncrement = incrementValue + (Math.random() * 2 - 1) * jitter;
    };

    const runtime = new Map<number, ReelRuntime>();
    const stepTimeouts = new Set<number>();

    const stepReel = (place: number, el: HTMLSpanElement) => {
      const state: ReelRuntime = runtime.get(place) ?? {
        displayed: digitAtPlace(initialValue, place),
        animating: false,
      };

      // skip stepping logic if the reel is already animating
      if (state.animating) {
        runtime.set(place, state);
        return;
      }

      const target = digitAtPlace(liveValue, place);
      if (target === state.displayed) {
        runtime.set(place, state);
        return;
      }

      const rollMs = cascadeStepMs(place, liveIncrement);
      const wrapping = state.displayed === DIGITS_PER_REVOLUTION - 1;

      state.animating = true;
      el.style.transition = `transform ${rollMs}ms ${CASCADE_EASING}`;
      el.style.transform = reelTransform(
        wrapping ? DIGITS_PER_REVOLUTION : state.displayed + 1
      );
      runtime.set(place, state);

      const timeoutId = window.setTimeout(() => {
        stepTimeouts.delete(timeoutId);
        if (wrapping) {
          el.style.transition = 'none';
          el.style.transform = reelTransform(0);
          state.displayed = 0;
        } else {
          state.displayed += 1;
        }
        state.animating = false;
        runtime.set(place, state);
      }, rollMs);

      stepTimeouts.add(timeoutId);
    };

    const tick = () =>
      cascadeRefs.current.forEach((el, place) => stepReel(place, el));

    const maxDigitsPerSecond = MAX_REEL_REVS_PER_S * DIGITS_PER_REVOLUTION;
    const freeRunPosition = new Map<number, number>();

    const positionContinuousReels = (elapsedMs: number) => {
      continuousRefs.current.forEach((el, place) => {
        const digitsPerSecond = reelDigitsPerSecond(place, liveIncrement);

        /*
         * Slow enough to read: lock straight to the live value so the digit and
         * its carries stay exact.
         */
        if (digitsPerSecond <= maxDigitsPerSecond) {
          const truePosition = phaseOf(liveValue, place);
          freeRunPosition.set(place, truePosition);
          el.style.transform = reelTransform(truePosition);
          return;
        }

        /*
         * Too fast to read: free-run at the capped speed from wherever it is so
         * the blur stays smooth instead of strobing.
         */
        const from = freeRunPosition.get(place) ?? phaseOf(liveValue, place);
        const next =
          (from + (elapsedMs / MS_PER_SECOND) * maxDigitsPerSecond) %
          DIGITS_PER_REVOLUTION;
        freeRunPosition.set(place, next);
        el.style.transform = reelTransform(next);
      });
    };

    let frameId = 0;
    let lastTickAt = 0;
    let lastFrameAt = 0;
    const loop = (now: number) => {
      let elapsedMs = 0;
      if (lastFrameAt !== 0) {
        elapsedMs = Math.min(now - lastFrameAt, MAX_FRAME_GAP_MS);

        if (now >= nextRerollAt) {
          rerollTarget();
          nextRerollAt = now + INCREMENT_REROLL_MS;
        }
        const smoothing = 1 - Math.exp(-elapsedMs / INCREMENT_SMOOTH_TAU_MS);
        liveIncrement += (targetIncrement - liveIncrement) * smoothing;

        liveValue += (elapsedMs / MS_PER_SECOND) * liveIncrement;

        // only grow the reel count when the value rolls past a power-of-ten boundary
        if (liveValue >= nextDigitThreshold) {
          const grownDigitCount = digitCountOf(Math.floor(liveValue));
          nextDigitThreshold = 10 ** grownDigitCount;
          setDigitCount(grownDigitCount);
        }
      }
      lastFrameAt = now;

      positionContinuousReels(elapsedMs);

      if (now - lastTickAt >= CASCADE_TICK_MS) {
        lastTickAt = now;
        tick();
      }
      frameId = window.requestAnimationFrame(loop);
    };

    const start = () => {
      if (frameId !== 0) return;
      /*
       * Reset the frame clock so re-entering view never credits time spent
       * paused to the counter.
       */
      lastFrameAt = 0;
      frameId = window.requestAnimationFrame(loop);
    };
    const stop = () => {
      if (frameId === 0) return;
      window.cancelAnimationFrame(frameId);
      frameId = 0;
    };

    loopControlRef.current = { start, stop };
    start();

    return () => {
      loopControlRef.current = undefined;
      stop();
      stepTimeouts.forEach(id => window.clearTimeout(id));
    };
  }, [incrementValue, initialValue]);

  useEffect(() => {
    const control = loopControlRef.current;
    if (!control) return;
    if (onScreen) {
      control.start();
    } else {
      control.stop();
    }
  }, [onScreen]);

  return (
    <CounterHeading
      ref={rootRef}
      fontSize={['2.1em', 8, 10]}
      color="white"
      textAlign="center"
      fontFamily="header"
      aria-label="Total amount saved by South Africans, counting up in real time"
    >
      <Separator>R</Separator>
      {tokens.map(token =>
        token.kind === 'separator' ? (
          <Separator key={`sep-${token.visualIndex}`}>{token.char}</Separator>
        ) : (
          <DigitReel
            key={token.place}
            place={token.place}
            initialValue={initialValue}
            incrementValue={incrementValue}
            registerReel={registerReel}
          />
        )
      )}
    </CounterHeading>
  );
};

export default BhagCounter;
